import Tag, { ITag, ITagApiModel, MaybeUndef } from '@models/Tag.model';
import { getDefaultTagBackground } from '@utils/getDefaultTagBackground';
import { nullToUndef } from '../utils/nullToUndef/nullToUndef';
import { Nullable } from './EquipmentInventory.model';

export enum CategoryColor {
  VIOLET = '#E7E4F9',
  GREEN = '#DCF2EA',
  YELLOW = '#FFEFD2',
  TEAL = '#D3F5F7',
  ORANGE = '#F8E3DA',
  PINK = '#FBDDF3',
  RED = '#F9DADA',
  BLUE = '#E6EDFF',
  UNCATEGORIZED = '#EDEFF4',
}

export interface ITagCategory {
  id: string;
  name: string;
  description: MaybeUndef<string>;
  showOnOnboard: boolean;
  isUnique: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastUsed: MaybeUndef<Date>;
  tags: MaybeUndef<ITag[]>;
  tagsCount: MaybeUndef<number>;
  /**
   * This can be null, but we defer to the default value
   */
  color: CategoryColor;
}

export interface ITagCategoryApiModel {
  id: string;
  name: string;
  description: Nullable<string>;
  showOnOnboard: Nullable<boolean>;
  isUnique: Nullable<boolean>;
  createdAt: string;
  updatedAt: string;
  lastUsed: Nullable<string>;
  tagsCount: Nullable<number>;
  color: Nullable<CategoryColor>;
}
export interface ITagCategoryApiModel2 {
  id: string;
  name: string;
  color: CategoryColor;
  description: string | null;
}
export interface ITagCategoryApiModelWithTags {
  id: string;
  name: string;
  description: Nullable<string>;
  showOnOnboard: Nullable<boolean>;
  isUnique: Nullable<boolean>;
  createdAt: string;
  updatedAt: string;
  lastUsed: Nullable<string>;
  tagsCount: Nullable<number>;
  /** The api should not send a null color, but this is a problem with colors that there are nulls, the default isused instaed */
  color: Nullable<CategoryColor>;
  tags: ITagApiModel[];
}

/**
 * This is supposed to be an interface compatible with ITagCategory that supplements ITagRaw info.
 */
export interface ITagCategoryLimit {
  color: CategoryColor;
  id: string;
  name: string;
}
/** This type ensures that whatever comes FROM a base, should be assignable to the properties of the base it extends, without any conflict. */

export interface ITagCategoryLimitWithDesc extends ITagCategoryLimit {
  description: ITagCategory['description'];
}
export default class TagCategory implements ITagCategory {
  public readonly id: string;

  public readonly name: string;

  public readonly description: MaybeUndef<string>;

  public readonly showOnOnboard: boolean;

  public readonly isUnique: boolean;

  public readonly createdAt: Date;

  public readonly updatedAt: Date;

  public readonly lastUsed: MaybeUndef<Date>;

  // Todo: Check category properties, tagsCount is being used as is though it could be undefined
  public readonly tagsCount: MaybeUndef<number>;

  public readonly color: CategoryColor;

  // Just with categoryModel with tags
  public readonly tags: MaybeUndef<ITag[]>;

  constructor(
    id: string,
    name: string,
    createdAt: Date,
    updatedAt: Date,
    showOnOnboard: boolean,
    isUnique: boolean,
    lastUsed: MaybeUndef<Date>,
    color: CategoryColor,
    tagsCount: MaybeUndef<number>,
    description: MaybeUndef<string>,
    tags: MaybeUndef<ITag[]>,
  ) {
    this.id = id;
    this.name = name;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.updatedAt = updatedAt;
    this.showOnOnboard = showOnOnboard;
    this.isUnique = isUnique;
    this.lastUsed = lastUsed;
    this.color = color;
    this.tagsCount = tagsCount;
    this.description = description;
    this.tags = tags;
  }

  static fromApiModel(apiModel: ITagCategoryApiModel) {
    const { id, name, description, color, tagsCount } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));
    const lastUsed = apiModel.lastUsed
      ? new Date(Date.parse(apiModel.lastUsed))
      : undefined;
    const showOnOnboard = apiModel.showOnOnboard
      ? apiModel.showOnOnboard
      : false;
    const isUnique = apiModel.isUnique ? apiModel.isUnique : false;

    return new TagCategory(
      id,
      name,
      createdAt,
      updatedAt,
      showOnOnboard,
      isUnique,
      lastUsed,
      getDefaultTagBackground(nullToUndef(color)),
      nullToUndef(tagsCount),
      nullToUndef(description),
      undefined,
    );
  }

  static fromApiModeWithTags(apiModel: ITagCategoryApiModelWithTags) {
    const { id, name, description, color, tagsCount } = apiModel;

    const createdAt = new Date(Date.parse(apiModel.createdAt));
    const updatedAt = new Date(Date.parse(apiModel.updatedAt));
    const lastUsed = apiModel.lastUsed
      ? new Date(Date.parse(apiModel.lastUsed))
      : undefined;
    const tags = apiModel.tags
      ? Tag.arrayFromApiModel(apiModel.tags)
      : undefined;
    const showOnOnboard = apiModel.showOnOnboard
      ? apiModel.showOnOnboard
      : false;
    const isUnique = apiModel.isUnique ? apiModel.isUnique : false;
    return new TagCategory(
      id,
      name,
      createdAt,
      updatedAt,
      showOnOnboard,
      isUnique,
      lastUsed,
      getDefaultTagBackground(nullToUndef(color)),
      nullToUndef(tagsCount),
      nullToUndef(description),
      tags,
    );
  }

  static arrayFromApiModelWithTags(
    arrayApiModel: ITagCategoryApiModelWithTags[],
  ) {
    const array = arrayApiModel.map(apiModel =>
      TagCategory.fromApiModeWithTags(apiModel),
    );

    return array;
  }
}
